import * as React from "react";
import { usePage } from "../routes";
import { Box, Button, TextField, Snackbar } from "@mui/material";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAccountCreation } from "../components/register/useAccountCreation";
import { RegistrationAlert, SnackSeverity } from "../components/register/SnackbarComponents";
import { RegistrationBox, RegistrationGrid } from "../components/register/RegistrationComponents";
import { useValidateEmail, useValidateSerialHash } from "../components/register/Validation";
import { styled } from "@mui/material/styles";

const CenteredDiv = styled("div")`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #f8f8f8;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

export function Register() {
  const { t } = useTranslation();
  const page = usePage(Register.name);
  const [email, setEmail] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "info" | "warning" | "error";
  }>({ open: false, message: "", severity: SnackSeverity.SUCCESS });
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [searchParams] = useSearchParams();
  const handleError = async (error: Response) => {
    let errorMessage = await error.text();
    if (error.status === 500 || errorMessage == null || errorMessage === "") {
      showError(t("common.dataError", { val: "register.title" }));
      return error;
    }
    try {
      showError(errorMessage);
    } catch (e) {
      showError(t("common.dataError", { val: "register.title" }));
    }
    return error;
  };

  const { mutate, isSuccess: successfulSubmit } = useAccountCreation({
    onError: handleError,
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showError = (message: string) => {
    setDisableSubmit(false);
    setSnackbar({
      severity: SnackSeverity.ERROR,
      message: message,
      open: true,
    });
  };

  const emailErrorMessage = useValidateEmail(email);
  const validateSerialHash = useValidateSerialHash();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setDisableSubmit(true);

    if (emailErrorMessage) {
      showError(emailErrorMessage);
      return;
    }

    const serialHashErrorMessage = validateSerialHash(searchParams.get("serialHash"));
    if (serialHashErrorMessage) {
      showError(serialHashErrorMessage);
      return;
    }

    mutate({ email: email, serialHash: searchParams.get("serialHash")! });
  };

  return (
    <React.Fragment>
      <RouteBreadcrumbs customPageTitle={`${page?.title}`} />
      {!successfulSubmit && (
        <RegistrationGrid container direction="column" justifyContent="center" alignItems="center">
          <RegistrationBox>
            <TextField
              fullWidth
              type="email"
              size="medium"
              label={t("login.email")}
              variant="outlined"
              value={email}
              disabled={disableSubmit}
              onChange={(e) => setEmail(e.target.value)}
            />
          </RegistrationBox>
          <Box mt={2}>
            <Button variant="contained" onClick={handleSubmit} color="primary" disabled={disableSubmit}>
              Register
            </Button>
          </Box>
        </RegistrationGrid>
      )}
      {successfulSubmit && <CenteredDiv>{t("register.successfulRegistration")}</CenteredDiv>}
      <Snackbar
        id={"snackbar"}
        open={snackbar.open}
        message={snackbar.message}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <RegistrationAlert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </RegistrationAlert>
      </Snackbar>
    </React.Fragment>
  );
}
